import React, { forwardRef, useImperativeHandle, useRef } from "react";
import styled, { css } from "styled-components";
import { mobile } from "@ui/styles/utils";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ChevronDownIcon } from "@ui/components/icons";
import { Menu } from "@ui/components/menus/menu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";

export interface SelecterRef {
	focus: () => void;
}
interface SelecterProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	value?: string;
	placeholder?: string;
	onChange: (v: string) => void;
	options: { value: string; label: string }[];
	disabled?: boolean;
	chevronColor?: string;
	light?: boolean;
	menuHeight?: number | undefined;
	showShevron?: boolean;
	customStyle?: boolean;
	loading?: boolean;
}

export const Selecter = forwardRef<SelecterRef, SelecterProps>(
	(
		{
			showShevron = true,
			value,
			placeholder,
			onChange,
			menuHeight = undefined,
			options,
			disabled,
			chevronColor,
			light,
			customStyle,
			loading,
			...htmlProps
		},
		ref
	) => {
		const selectRef = useRef<HTMLSelectElement>(null);
		const currentLabel = options.find((op) => op.value === value)?.label;

		// const customSelectorStyle: CSSObject = {
		// 	background: "rgba(243, 244, 246, 0.4)",
		// 	border: "1.5px solid #E9E9E9",
		// 	borderRadius: "7px",
		// 	height: "45px",
		// };

		useImperativeHandle(ref, () => ({
			focus: () => selectRef.current?.focus(),
		}));

		return (
			<Menu
				{...htmlProps}
				align="left"
				bottomSheetMode
				menuHeight={menuHeight}
				closeOnSelect
				renderMenuContent={() => (
					<MenuContent>
						{options.map((opt) => (
							<MenuRow
								key={opt.value}
								onClick={() => {
									onChange(opt.value);
								}}
							>
								{opt.label}
							</MenuRow>
						))}
					</MenuContent>
				)}
			>
				<Container light={light} customStyle={customStyle}>
					<Content>
						{currentLabel ?? <Placeholder>{placeholder}</Placeholder>}
						{showShevron && <StyledChevronDownIcon color={chevronColor} />}
					</Content>
				</Container>
			</Menu>
		);
	}
);

const Container = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => !["light", "customStyle"].includes(prop),
})<{ light?: boolean; customStyle?: boolean }>`
	padding: ${({ light }) => (light ? "9px 16px 8px" : "10px 16px")};
	display: flex;
	align-items: center;
	cursor: pointer;

	${({ customStyle }) =>
		customStyle &&
		css`
			background: rgba(243, 244, 246, 0.4);
			border: 1.5px solid #e9e9e9;
			borde-radius: 7px;
			height: 45px;
		`}

	position: relative;
	svg {
		pointer-events: none;
		margin-left: 8px;
	}
`;

const Content = styled.div`
	${({ theme }) => theme.textStyles.primary};
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const StyledChevronDownIcon = styled(ChevronDownIcon)<{ color?: string }>`
	path:last-of-type {
		fill: ${({ theme, color }) => color ?? theme.colors.black};
	}
`;

const Placeholder = styled.span`
	color: ${({ theme, color }) => color ?? theme.colors.textPrimary};
	opacity: 0.6;
`;

const MenuContent = styled(SimpleMenuContent)`
	min-width: 100px;
	@media ${mobile} {
		padding-bottom: 50px;
	}

	> * {
		font-weight: initial;
		transition: color 0.1s linear;
		&:hover {
			color: ${({ theme }) => theme.colors.primary};
		}
	}
`;
